import React, { useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { ArkorbitGridBackground } from '../arkorbit-ui/ArkorbitGridBackground';
import { BRAND_PORTAL } from '../../constants';
import type { CustomerRecord } from '../CustomerLogin';
import { PORTAL_SHELL } from './portalTheme';
import { portalViewTitle, type PortalView } from './portalNavigation';
import { PortalShortcutProvider, usePortalShortcutMap } from './portalShortcutContext';
import { portalShellSurfaceClass } from './portalAppearance';
import { PortalAppearanceProvider, usePortalAppearance } from './portalAppearanceContext';
import { PortalAppearanceSwitch } from './PortalAppearanceSwitch';
import { requestSitzGlockeOpen } from './sitzGlockeStore';
import {
  activatePortalShortcutButton,
  dispatchPortalShortcutKey,
  isPortalShortcutCaptureActive,
  isPortalShortcutTypingTarget,
  portalShortcutZurueckFromTypingTarget,
} from './portalShortcuts';
import { PortalSidebar, type PortalSidebarProps } from './PortalSidebar';
import { PortalCommandPalette } from './PortalCommandPalette';
import { MerkzettelDock } from './MerkzettelDock';
import { PortalSitzGlocke } from './PortalSitzGlocke';

export interface PortalShellProps {
  customer: { kanzlei: string; email: string; tier: 'standard' | 'premium'; account_kind?: CustomerRecord['account_kind'] };
  /** Element am eingeloggten Sitz — Reiter Kanzlei-Chat. */
  elementSeat?: boolean;
  documentCount?: number | null;
  view: PortalView;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  onLogout: () => void;
  settingsPanel: React.ReactNode;
  children: React.ReactNode;
  sidebarProps?: Partial<Pick<PortalSidebarProps, 'onNavigate' | 'isPremium' | 'mehrExpanded' | 'onMehrExpandedChange'>>;
  onNavigate: (view: PortalView) => void;
  isPremium: boolean;
  contractGeneratorEnabled?: boolean;
  /** MEMCORE Modul B (Labor): „Mein Gedächtnis“ unter Mehr. */
  memcoreEnabled?: boolean;
  teamAccessEnabled?: boolean;
  /** Optional: Fokus ARKO-Input bei `/` */
  onSlashFocus?: () => void;
  /** Chat/Editor: volle Höhe ohne äußeren Scroll */
  fillHeight?: boolean;
  /** Dokument+ARKO Arbeitsmodus: Sidebar/Mobile-Bar/Padding ausblenden */
  minimalChrome?: boolean;
  /** Sitz für Merkzettel-Pin (kein Portal-View). */
  customerId?: string;
  kanzleiId?: string;
  merkzettelEnabled?: boolean;
}

export const PortalShell: React.FC<PortalShellProps> = (props) => (
  <PortalShortcutProvider seatId={props.customerId ?? ''}>
    <PortalAppearanceProvider seatId={props.customerId ?? ''}>
      <PortalShellFrame {...props} />
    </PortalAppearanceProvider>
  </PortalShortcutProvider>
);

const PortalShellFrame: React.FC<PortalShellProps> = ({
  customer,
  elementSeat = false,
  documentCount,
  view,
  mobileOpen,
  onMobileOpenChange,
  settingsOpen,
  onSettingsOpenChange,
  onLogout,
  settingsPanel,
  children,
  onNavigate,
  isPremium,
  contractGeneratorEnabled,
  memcoreEnabled,
  teamAccessEnabled = false,
  onSlashFocus,
  fillHeight = false,
  minimalChrome = false,
  customerId,
  kanzleiId,
  merkzettelEnabled = false,
}) => {
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [merkzettelPinRoot, setMerkzettelPinRoot] = useState<HTMLDivElement | null>(null);
  const shortcutMap = usePortalShortcutMap();
  const lightShell = usePortalAppearance() === 'light';
  const shellMark = portalShellSurfaceClass(lightShell ? 'light' : 'dark');
  const appGround = lightShell
    ? 'flex h-screen bg-[#e6e1d8] text-[#1c1915] overflow-hidden'
    : PORTAL_SHELL.app;

  useEffect(() => {
    document.documentElement.setAttribute('data-portal-root', '');
    return () => {
      document.documentElement.removeAttribute('data-portal-root');
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (!lightShell) {
      html.style.backgroundColor = '';
      body.style.backgroundColor = '';
      return;
    }
    html.style.backgroundColor = '#e6e1d8';
    body.style.backgroundColor = '#e6e1d8';
    return () => {
      html.style.backgroundColor = '';
      body.style.backgroundColor = '';
    };
  }, [lightShell]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isPortalShortcutCaptureActive()) return;
      const plainEscape = e.key === 'Escape' && !e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setCmdkOpen(true);
        return;
      }
      if (cmdkOpen) return;
      if (plainEscape && settingsOpen) {
        onSettingsOpenChange(false);
        return;
      }
      if (plainEscape && mobileOpen) {
        onMobileOpenChange(false);
        return;
      }
      if (
        e.key === '/' &&
        !settingsOpen &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        !(document.activeElement as HTMLElement)?.isContentEditable
      ) {
        e.preventDefault();
        e.stopPropagation();
        onSlashFocus?.();
        return;
      }
      const action = dispatchPortalShortcutKey(e, {
        map: shortcutMap,
        typing: isPortalShortcutTypingTarget(document.activeElement),
        zurueckInTitle: portalShortcutZurueckFromTypingTarget(document.activeElement),
      });
      if (action.kind === 'none') {
        if (plainEscape) window.dispatchEvent(new CustomEvent('arkorbit:portal-escape'));
        return;
      }
      if (action.kind === 'press') {
        activatePortalShortcutButton(action.button);
        if (plainEscape) window.dispatchEvent(new CustomEvent('arkorbit:portal-escape'));
        return;
      }
      if (action.kind === 'glocke') {
        requestSitzGlockeOpen();
        return;
      }
      onNavigate(action.view);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [cmdkOpen, settingsOpen, mobileOpen, onSettingsOpenChange, onMobileOpenChange, onSlashFocus, onNavigate, shortcutMap]);

  const handleNavigate = (v: PortalView) => {
    onNavigate(v);
    onMobileOpenChange(false);
  };

  const merkzettelPinSlot =
    merkzettelEnabled && customerId ? (
      <div
        ref={setMerkzettelPinRoot}
        data-testid={minimalChrome ? 'portal-merkzettel-rail' : 'portal-merkzettel-pin-slot'}
        className={minimalChrome ? 'fixed left-2 top-[28%] z-[45]' : 'pt-0.5'}
      />
    ) : null;

  const sitzGlockeSidebar =
    customerId ? (
      <PortalSitzGlocke customerId={customerId} kanzleiId={kanzleiId} placement="sidebar" />
    ) : null;
  const sitzGlockeMobile =
    customerId ? (
      <PortalSitzGlocke customerId={customerId} kanzleiId={kanzleiId} placement="mobile" />
    ) : null;

  return (
    <div
      data-testid="portal-shell"
      data-portal-root=""
      className={`${appGround} ${shellMark}${minimalChrome ? ' portal-doc-work-mode-active' : ''}`}
    >
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          aria-label="Menü schließen"
          onClick={() => onMobileOpenChange(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 flex h-full min-h-0 transform ${
          minimalChrome ? 'hidden' : mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:translate-x-0 transition-transform duration-200 ease-out`}
      >
        <PortalSidebar
          customer={customer}
          elementSeat={elementSeat}
          documentCount={documentCount}
          merkzettelPin={!minimalChrome ? merkzettelPinSlot : null}
          sitzGlocke={!minimalChrome ? sitzGlockeSidebar : null}
          isPremium={isPremium}
          contractGeneratorEnabled={contractGeneratorEnabled}
          memcoreEnabled={memcoreEnabled}
          teamAccessEnabled={teamAccessEnabled}
          view={view}
          settingsOpen={settingsOpen}
          onNavigate={handleNavigate}
          onOpenSettings={() => {
            onSettingsOpenChange(true);
            onMobileOpenChange(false);
          }}
          onLogout={() => {
            onLogout();
            onMobileOpenChange(false);
          }}
        />
      </div>

      {minimalChrome ? merkzettelPinSlot : null}

      <main className={`${PORTAL_SHELL.mainArea} ${lightShell ? 'bg-[#e6e1d8]' : ''}`}>
        {!minimalChrome && (
          <div className={lightShell ? 'h-px w-full bg-[#e6e1d8] flex-shrink-0' : PORTAL_SHELL.topBorder} />
        )}

        <div className={`${lightShell ? 'md:hidden flex items-center justify-between px-3 py-3 bg-[#e6e1d8] text-[#1c1915] border-b border-[#6a5684] flex-shrink-0' : PORTAL_SHELL.mobileBar} ${shellMark} ${minimalChrome ? 'hidden' : ''}`}>
          <button
            type="button"
            onClick={() => onMobileOpenChange(true)}
            className={lightShell ? 'text-[#1c1915] p-2 hover:bg-[#f3efe8] rounded-lg' : 'text-slate-300 p-2 hover:bg-white/[0.06] rounded-lg'}
            aria-label="Menü öffnen"
          >
            <Menu className="w-5 h-5" />
          </button>
          <PortalAppearanceSwitch className="max-w-[9.5rem] flex-shrink-0" />
          <span className={`text-sm font-medium truncate flex-1 text-center px-2 ${lightShell ? 'text-[#1c1915]' : 'text-white'}`}>
            {portalViewTitle(view)}
          </span>
          {sitzGlockeMobile || (
            <span className={`w-9 text-[10px] truncate text-right ${lightShell ? 'text-[#3f3832]' : 'text-slate-600'}`}>{customer.kanzlei}</span>
          )}
        </div>

        <div
          className={`relative ${lightShell ? 'bg-[#e6e1d8]' : ''} ${
            fillHeight
              ? 'flex-1 min-h-0 flex flex-col overflow-hidden'
              : PORTAL_SHELL.mainScroll
          }`}
        >
          <ArkorbitGridBackground opacity={minimalChrome ? 0 : 0.035} />
          <div
            className={`${PORTAL_SHELL.mainContent} relative z-[1] ${
              minimalChrome ? '!p-0' : ''
            } ${fillHeight ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : ''}`}
          >
            {children}
          </div>
        </div>
      </main>

      {settingsOpen && (
        <>
          <button
            type="button"
            className={PORTAL_SHELL.settingsOverlay}
            aria-label="Einstellungen schließen"
            onClick={() => onSettingsOpenChange(false)}
          />
          <aside className={PORTAL_SHELL.settingsPanel} role="dialog" aria-label="Einstellungen">
            {settingsPanel}
          </aside>
        </>
      )}

      <PortalCommandPalette
        open={cmdkOpen}
        onOpenChange={setCmdkOpen}
        elementSeat={elementSeat}
        onNavigate={handleNavigate}
        onOpenSettings={() => {
          onSettingsOpenChange(true);
          onMobileOpenChange(false);
        }}
        onFocusArko={onSlashFocus}
      />

      <div data-testid="portal-merkzettel-host" className="contents">
        {merkzettelEnabled && customerId ? (
          <MerkzettelDock
            customerId={customerId}
            kanzleiId={kanzleiId}
            variant={minimalChrome ? 'rail' : 'sidebar'}
            pinRoot={merkzettelPinRoot}
          />
        ) : null}
      </div>
    </div>
  );
};
